import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { login, register, clearError } from "../../features/authSlice";
import { PASSWORD_POLICY_HINT, validatePasswordStrength } from "../../utils/passwordValidation";
import styles from "./authForm.module.scss";

const AuthForm = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, token } = useSelector((state) => state.auth);

  useEffect(() => {
    if (token) {
      // Rediriger vers l'URL précédente ou vers /home par défaut
      const from = location.state?.from?.pathname || "/home";
      navigate(from, { replace: true });
    }
  }, [token, navigate, location]);

  const validateForm = () => {
    const newErrors = {};
    if (!isLogin && !formData.username) {
      newErrors.username = "Le nom d'utilisateur est requis";
    }
    if (isLogin && !formData.username) {
      newErrors.username = "Le nom d'utilisateur est requis";
    }
    if (!isLogin && !formData.email) {
      newErrors.email = "L'email est requis";
    } else if (!isLogin && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "L'email n'est pas valide";
    }
    if (!formData.password) {
      newErrors.password = "Le mot de passe est requis";
    } else if (!isLogin) {
      const pwdCheck = validatePasswordStrength(formData.password);
      if (!pwdCheck.valid) {
        newErrors.password = pwdCheck.message;
      }
    }
    if (!isLogin && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Les mots de passe ne correspondent pas";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const { confirmPassword, email, ...submitData } = formData;
    try {
      if (isLogin) {
        await dispatch(login(submitData)).unwrap();
      } else {
        await dispatch(register({ ...submitData, email })).unwrap();
        // Rediriger vers la page de vérification après l'inscription
        navigate("/verify-email");
      }
    } catch (err) {
      console.error("Erreur d'authentification:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  return (
    <div className={styles.authForm}>
      <h2>{isLogin ? "Connexion" : "Inscription"}</h2>
      {error && <div className={styles.error}>{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label htmlFor="username">Nom d'utilisateur</label>
          <input
            type="text"
            id="username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            className={errors.username ? styles.errorInput : ""}
          />
          {errors.username && (
            <span className={styles.error}>{errors.username}</span>
          )}
        </div>
        {!isLogin && (
          <div className={styles.formGroup}>
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? styles.errorInput : ""}
            />
            {errors.email && (
              <span className={styles.error}>{errors.email}</span>
            )}
          </div>
        )}
        <div className={styles.formGroup}>
          <label htmlFor="password">Mot de passe</label>
          {!isLogin && (
            <p className={styles.passwordHint}>{PASSWORD_POLICY_HINT}</p>
          )}
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className={errors.password ? styles.errorInput : ""}
          />
          {errors.password && (
            <span className={styles.error}>{errors.password}</span>
          )}
        </div>
        {!isLogin && (
          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={errors.confirmPassword ? styles.errorInput : ""}
            />
            {errors.confirmPassword && (
              <span className={styles.error}>{errors.confirmPassword}</span>
            )}
          </div>
        )}
        <button type="submit" disabled={loading}>
          {loading ? "Chargement..." : isLogin ? "Se connecter" : "S'inscrire"}
        </button>
      </form>
      {isLogin && (
        <div className={styles.forgotPassword}>
          <Link to="/forgot-password">Mot de passe oublié ?</Link>
        </div>
      )}
      <p>
        {isLogin ? "Pas encore de compte ?" : "Déjà un compte ?"}{" "}
        <button
          type="button"
          className={styles.switchButton}
          onClick={() => {
            setIsLogin(!isLogin);
            setErrors({});
            dispatch(clearError());
          }}
        >
          {isLogin ? "S'inscrire" : "Se connecter"}
        </button>
      </p>
    </div>
  );
};

export default AuthForm;
